import {computed, observable, action} from 'mobx';
import {MeetingMode, RECORDING_STATUS} from '@techsee/techsee-common/lib/constants/room.constants';
import {VIDEO_MODES, IMAGE_MODES} from '../../../../states/dashboard/main/main.settings';
import {CONNECTION_STATUS} from '../../../services/ts-locale-helper/ts-locale-helper.settings';
import get from 'lodash/get';
import {VisualScanStatusType, WifiScanStatusType} from '@techsee/techsee-common/lib/constants/utils.constant';
import {ISpeedtestResults} from '../../../services/SpeedtestService';

export default class FloatingToolbarState {
    @observable _isRunWifiButtonDisabled: boolean = false;
    @observable private isFlashlightAvailableOnClient: boolean = false;
    @observable private _toolbarPos: {x: number; y: number} = {x: -999, y: -999};
    constructor(dashboardState: any) {
        // Make dashboardState non-enumerable to prevent Angular's digest from traversing it
        Object.defineProperty(this, 'dashboardState', {
            value: dashboardState,
            writable: false,
            enumerable: false,
            configurable: false
        });
    }

    // Access dashboardState via type assertion since it's not in class properties
    private get ds(): any {
        return (this as any).dashboardState;
    }

    @computed
    get isRunWifiButtonDisabled() {
        const visualScanStatus = this.ds.clientRoomStorage.visualScanStatus;
        const isVisualScanActive = [VisualScanStatusType.Processing, VisualScanStatusType.InProgress].includes(
            visualScanStatus
        );

        return this._isRunWifiButtonDisabled || isVisualScanActive;
    }

    @computed
    get isStopVisualWifiScan() {
        return this.ds.clientRoomStorage.visualScanStatus === VisualScanStatusType.InProgress;
    }

    @computed
    get isDesktopSharingRemoteControlSession(): boolean {
        return (
            this.ds.isDesktopSharingSession &&
            get(this.ds.accountData, 'protectedSettings.desktopSharing.enableRemoteControl')
        );
    }

    @computed
    get displaySaveButton(): boolean {
        if (this.ds.isLibraryTaggingMode) {
            return true;
        }
        return (
            get(this.ds.accountData, 'protectedSettings.showSaveButton') &&
            !this.ds.isObserve &&
            (this.ds.connectionStatus !== CONNECTION_STATUS.WAITING || this.ds.isOfflineRoom) &&
            (this.ds.canvasReady || this.ds.videoModeState.isVideoControlActive)
        );
    }

    @computed
    get displayFloatingToolbarSpeedtest(): boolean {
        return (
            get(this.ds.accountData, 'protectedSettings.mobileSpeedtest.enabled') &&
            ![MeetingMode.screen, MeetingMode.appSharing].includes(this.ds.meetingMode as MeetingMode)
        );
    }
    @computed
    get displayFloatingToolbarFlashlight(): boolean {
        return (
            get(this.ds.accountData, 'protectedSettings.enableFlashlight') &&
            (this.ds.isLiveVideo || this.ds.isVideoScan) &&
            ![MeetingMode.screen, MeetingMode.appSharing].includes(this.ds.meetingMode as MeetingMode)
        );
    }

    @computed
    get disabledSaveButton(): boolean {
        return this.ds.canvasIsFresh && !this.ds.areTagsChanged && !this.ds.videoModeState.isVideoControlActive;
    }

    @computed
    get isRunWifiVisualScanButtonDisabled() {
        const isCapable = get(this.ds, 'isDeviceSupportsVisualScan', true);

        if (this.isStopVisualWifiScan) {
            return false;
        }

        const isWifiScanInProgress = this.ds.clientRoomStorage?.wifiScanStatus === WifiScanStatusType.InProgress;
        const isVisualScanProcessing = this.ds.clientRoomStorage?.visualScanStatus === VisualScanStatusType.Processing;
        const isVisualScanInProgress = this.ds.clientRoomStorage?.visualScanStatus === VisualScanStatusType.InProgress;
        const isDashboardVisualScanInit = this.ds.dashboardRoomStorage?.visualScanStatus === VisualScanStatusType.Init;
        const isOCRProcessing = this.ds.isScanMode;

        return (
            (!this.ds.isScreenMirroring && !this.ds.isVideoApplicationMode) ||
            !this.ds.clientRoomStorage.connected ||
            isWifiScanInProgress ||
            (isDashboardVisualScanInit && !isVisualScanInProgress) ||
            isVisualScanProcessing ||
            !isCapable ||
            isOCRProcessing
        );
    }

    disabledSpeedtestButton(resultsReady: ISpeedtestResults) {
        return (
            this.ds.isObserve ||
            this.ds.displayLoaderReconnecting ||
            (this.ds.clientRoomStorage.mode === MeetingMode.screen && !this.ds.isDesktopSharingSession) ||
            this.ds.clientRoomStorage.mode === MeetingMode.appSharing ||
            (!resultsReady && (this.ds.endedTheMeeting || this.ds.customerEndedTheMeeting))
        );
    }

    get isRecordingEnabled() {
        return (
            !this.ds.offlineMode &&
            (this.ds.isMediaServer || this.ds.isOpentok) &&
            !this.ds.isObserve &&
            (get(this.ds, 'accountData.protectedSettings.recording.onDemand.enabled') ||
                get(this.ds, 'accountData.protectedSettings.recording.automatic.enabled')) &&
            this.ds.clientRoomStorage.mode === MeetingMode.video
        );
    }

    get recordButtonReady() {
        return (
            [RECORDING_STATUS.ready, RECORDING_STATUS.started].includes(this.ds.dashboardRoomStorage.recordingStatus) &&
            this.ds.areBothSidesConnected
        );
    }

    @computed
    get isOnDemandRecordingMode(): boolean {
        return !!get(this.ds, 'accountData.protectedSettings.recording.onDemand.enabled');
    }

    /** Manual recording was disabled for this session by `disableManualRecording` on the Session Initiation API */
    @computed
    get isManualRecordingDisabled(): boolean {
        return !!get(this.ds, 'roomData.disableManualRecording', false);
    }

    /** Whether the manual Start/Stop recording controls should be exposed to the agent */
    @computed
    get displayOnDemandRecordingControls(): boolean {
        return this.isOnDemandRecordingMode && !this.isManualRecordingDisabled && this.isRecordingEnabled;
    }

    @computed
    get toolbarPos() {
        return this._toolbarPos;
    }

    @action
    setToolbarPos(x: number, y: number) {
        this._toolbarPos = {x, y};
    }
}
